import React, { ChangeEvent, KeyboardEvent } from "react";
import Logo from "../components/Logo";
import AuthControls from "../components/AuthControls";
import { useLang } from "../LangContext";

type MfaMethod = "totp" | "email";

interface Props {
  methods: MfaMethod[];
  activeMethod: MfaMethod;
  code: string;
  error: string;
  submitting: boolean;
  onCodeChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onResend: () => void;
  onSwitchMethod: (method: MfaMethod) => void;
}

export default function MfaChallenge({
  methods,
  activeMethod,
  code,
  error,
  submitting,
  onCodeChange,
  onSubmit,
  onCancel,
  onResend,
  onSwitchMethod,
}: Props) {
  const { t } = useLang();
  const bothAvailable = methods.length > 1;
  return (
    <main className="login-container">
      <div className="login-wrapper">
      <div className="login-top-controls">
        <AuthControls />
      </div>
      <div className="login-card">
        <div className="login-brand">
          <Logo size={40} withWordmark />
        </div>
        <h2>{t.mfaChallengeHeading}</h2>
        <div className="login-form">
          {bothAvailable && (
            <div className="mfa-method-tabs">
              <button
                className={`mfa-tab${activeMethod === "totp" ? " active" : ""}`}
                onClick={() => onSwitchMethod("totp")}
                type="button"
              >
                {t.mfaChallengeTabTotp}
              </button>
              <button
                className={`mfa-tab${activeMethod === "email" ? " active" : ""}`}
                onClick={() => onSwitchMethod("email")}
                type="button"
              >
                {t.mfaChallengeTabEmail}
              </button>
            </div>
          )}
          {!bothAvailable && activeMethod === "email" && (
            <p className="mfa-hint">{t.mfaChallengeHintEmail}</p>
          )}
          {!bothAvailable && activeMethod === "totp" && (
            <p className="mfa-hint">{t.mfaChallengeHintTotp}</p>
          )}
          <input
            className="input"
            placeholder={t.mfaCodePlaceholderChallenge}
            value={code}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onCodeChange(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && onSubmit()}
            maxLength={6}
            autoFocus
            disabled={submitting}
          />
          <button
            className="button login-button"
            onClick={onSubmit}
            disabled={submitting || code.length < 6}
          >
            {submitting ? t.mfaVerifying : t.mfaVerify}
          </button>
          {error && <div className="error-message">{error}</div>}
          {activeMethod === "email" && (
            <p>
              <button className="link-button" onClick={onResend} type="button">
                {t.mfaResendCode}
              </button>
            </p>
          )}
          <p>
            <button className="link-button" onClick={onCancel} type="button">
              {t.mfaBackToLogin}
            </button>
          </p>
        </div>
      </div>
      </div>
    </main>
  );
}
